export type PortalRosterRow = {
  id: string;
  fmbThali?: {
    thaliNo?: string | null;
    deliveryAddress?: string | null;
    deliveryMohallah?: string | null;
    thaliType?: { id?: string | null; name?: string | null; code?: string | null } | null;
    fmb?: {
      itsNo?: string | null;
      name?: string | null;
      whatsappNo?: string | null;
      mobileNo?: string | null;
    };
  };
};

export type PortalKitchenPickupRow = { id: string; label: string; count: number };

/** Kitchen pickup summary: configured types, optional other, then total. */
export function buildPortalKitchenPickupSummary(params: {
  thaliTypes: { id: string; name: string }[];
  roster: PortalRosterRow[];
}): PortalKitchenPickupRow[] {
  const byId = new Map(params.thaliTypes.map((t) => [t.id, 0]));
  let other = 0;
  for (const it of params.roster) {
    const typeId = it.fmbThali?.thaliType?.id ?? null;
    if (typeId && byId.has(typeId)) {
      byId.set(typeId, (byId.get(typeId) ?? 0) + 1);
    } else {
      other += 1;
    }
  }
  const rows: PortalKitchenPickupRow[] = params.thaliTypes.map((t) => ({
    id: t.id,
    label: t.name,
    count: byId.get(t.id) ?? 0,
  }));
  if (other > 0) {
    rows.push({ id: "__other", label: "Other", count: other });
  }
  rows.push({ id: "__total", label: "Total", count: params.roster.length });
  return rows;
}

export type PortalRosterStopView = {
  thaliNo: string;
  thaliType: string;
  address: string;
  name: string;
  phone: string;
  itsNo: string;
};

/** Address + mohallah for a delivery stop. */
export function portalRosterDeliveryLine(
  t: PortalRosterRow["fmbThali"] | null | undefined
): string {
  if (!t) return "";
  return [t.deliveryAddress, t.deliveryMohallah]
    .map((x) => (typeof x === "string" ? x.trim() : ""))
    .filter(Boolean)
    .join(" — ");
}

export function portalRosterEmptyMessage(params: {
  loading: boolean;
  hasDailyMenu: boolean;
  rosterCount: number;
  visibleCount: number;
}): string | null {
  if (params.loading) return "Loading…";
  if (!params.hasDailyMenu) return "No menu for this date — no deliveries.";
  if (params.rosterCount === 0) {
    return "No thalis on your roster for this date. Check assignments, suspensions, and whether this is a service day for each thali.";
  }
  if (params.visibleCount === 0) return "No rows match this filter.";
  return null;
}

type PortalRosterFmbContact = NonNullable<NonNullable<PortalRosterRow["fmbThali"]>["fmb"]>;

/** WhatsApp number if present, otherwise mobile. */
export function portalRosterPhone(fmb: PortalRosterFmbContact | null | undefined): string {
  const wa = fmb?.whatsappNo?.trim() ?? "";
  const mobile = fmb?.mobileNo?.trim() ?? "";
  return wa || mobile;
}

/** `tel:` href with digits and a leading `+` only. */
export function portalRosterTelHref(phone: string): string | null {
  const trimmed = phone.trim();
  if (!trimmed) return null;
  const plus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return null;
  return `tel:${plus ? `+${digits}` : digits}`;
}

/** Compact fields shown on a portal delivery card. */
export function portalRosterStopView(it: PortalRosterRow): PortalRosterStopView {
  const t = it.fmbThali;
  const fmb = t?.fmb;
  return {
    thaliNo: t?.thaliNo?.trim() || "—",
    thaliType: t?.thaliType?.name?.trim() || t?.thaliType?.code?.trim() || "",
    address: portalRosterDeliveryLine(t) || "—",
    name: fmb?.name?.trim() || "",
    phone: portalRosterPhone(fmb),
    itsNo: fmb?.itsNo?.trim() || "",
  };
}

/** Filter roster; relative API delivery order is preserved. */
export function filterPortalRoster(rows: PortalRosterRow[], search: string): PortalRosterRow[] {
  const q = search.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((it) => {
    const t = it.fmbThali;
    const hay = [
      t?.thaliNo,
      t?.fmb?.itsNo,
      t?.fmb?.name,
      t?.fmb?.whatsappNo,
      t?.fmb?.mobileNo,
      t?.deliveryAddress,
      t?.deliveryMohallah,
      t?.thaliType?.name,
      t?.thaliType?.code,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}
